import {ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {Status} from "../../content-layout/status-panel/status-panel.component";
import {StatusPanelService} from "../../content-layout/status-panel/status-panel.service";
import {ErrorHandlerService} from "../../../error-handler.service";
import {FUIAlertActions, FUIAlertService, FUISpinnersService} from "@fedelta-pos/ui";
import {CustomerInstanceVenueModel} from "../customer-instance-venue.model";
import {VenueMenuService} from "./venue-menu.service";
import {VenueMenuData} from "./venue-menu.data";
import {UserData} from "../../auth/user.data";
import {Subscription} from "rxjs";

@Component({
    selector: "fhm-menu",
    templateUrl: "./menu.component.html",
    styleUrls: ["./menu.component.scss"],
    providers: [VenueMenuService],
    standalone: false
})
export class MenuComponent implements OnInit, OnDestroy {

    Status = Status;
    UserData = UserData;

    private readonly MAX_UPLOAD_CONTRACT_SIZE = 10485760;

    private subscriptions: Subscription = new Subscription();

    @Input() loggedUser: UserData;

    @ViewChild("inputUploadFile", {read: ElementRef}) public inputUploadFile: ElementRef;

    constructor(private statusPanelService: StatusPanelService,
                public model: CustomerInstanceVenueModel,
                private spinnersService: FUISpinnersService,
                private errorHandler: ErrorHandlerService,
                private venueMenuService: VenueMenuService,
                private alertService: FUIAlertService,
                private changeDetectorRef: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        this.subscriptions.add(this.model.data$.subscribe(() => {
            this.changeDetectorRef.detectChanges();
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    uploadMenu(): void {
        this.inputUploadFile.nativeElement.click();
    }

    inputUploadContractClick(event: MouseEvent): void {
        event.stopPropagation();
    }

    fileSelected(event: Event): void {
        const fileList: FileList = (<HTMLInputElement>event.target).files;
        if (fileList && fileList.length > 0) {
            try {
                const file: File = fileList[0];
                if (file.size > this.MAX_UPLOAD_CONTRACT_SIZE) {
                    this.alertService.showError("fhm.contract.alert.uploadContractSizeLimit.title",
                        "fhm.contract.alert.uploadContractSizeLimit.message").subscribe(() => {});
                    return;
                }
                this.uploadMenuFile(file);
            } finally {
                (<HTMLInputElement>event.target).value = null;
            }
        }
    }

    async uploadMenuFile(file): Promise<void> {
        this.spinnersService.open();
        try {
            await this.venueMenuService.uploadMenu(this.model.venueId, file);
            await this.model.refresh();
            this.spinnersService.close();
        } catch (e) {
            this.spinnersService.close();
            this.errorHandler.handleErrorResponse(e);
        }
    }

    async downloadMenu(menu: VenueMenuData): Promise<void> {
        this.spinnersService.open();
        try {
            await this.venueMenuService.downloadMenu(this.model.venueId, menu.id, menu.fileName);
            this.spinnersService.close();
        } catch (e) {
            this.spinnersService.close();
            this.errorHandler.handleErrorResponse(e);
        }
    }

    async deleteMenu(menu: VenueMenuData): Promise<void> {
        this.alertService.showConfirmationOkCancel("fhm.menu.remove.title",
            "fhm.menu.remove.message").subscribe(async (action) => {
            if (action === FUIAlertActions.OK_ACTION) {
                this.spinnersService.open();
                try {
                    await this.venueMenuService.deleteMenu(this.model.venueId, menu.id);
                    this.model.data.menus = this.model.data.menus.filter(value => value.id !== menu.id);
                    this.spinnersService.close();
                } catch (e) {
                    this.spinnersService.close();
                    this.errorHandler.handleErrorResponse(e);
                }
            }
        });
    }
}
